import clsx from "clsx";
import React, { useMemo } from "react";
import { RelatedProductsSidebar } from "web/react/components/related-products-sidebar/related-products-sidebar";
import { Accordion } from "web/react/emo/accordion";
import { Button } from "web/react/emo/button";
import { Stack } from "web/react/emo/shared/components/stack";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useLeadLinkClick } from "web/react/hooks/use-lead-link-click/use-lead-link-click";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import { MoreLikeThis } from "web/react/pages/product/in-stock/in-stock-product-page/more-like-this/more-like-this";
import { ProductImageGallery } from "web/react/pages/product/product-image-gallery/product-image-gallery";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import * as styles from "./product-area.css";
import { ProductInfo } from "./product-info";

export function ProductArea(): React.ReactElement {
    const { isMobileViewport, isTabletViewport } = useDomViewport();

    const {
        designer,
        product,
        buyOptions,
        activeProduct: { longDescription },
        relatedProductsSidebar,
        productPurchaseType,
    } = useInStockProductPageContext();

    const showGalleryOnLeft = environment.getFeature("cw_show_gallery_on_left");
    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );
    const removeSizeAndFitAccordion =
        isDeepAffiliatePhase1Enabled || productPurchaseType === ProductPurchaseType.CHECKOUT;

    const { onClick, leadHref } = useLeadLinkClick({
        href: buyOptions[0].affiliate_url,
        beforeOnClick: (process, event) => {
            analytics.event({
                category: "buy_area",
                action: "clicked",
                label: "size_and_fit",
            });
            process(event);
        },
        reason: "retailer-available-sizes",
        openInNewTab: true,
    });

    const accordionContents = useMemo(() => {
        const productDetailsAccordion = {
            title: gettext("in_stock_product.details"),
            children: (
                <Text textStyle="body-s" color="secondary" dataTestId="product-description">
                    {longDescription}
                </Text>
            ),
            onClick: () => {
                analytics.event({
                    category: "product area",
                    action: "click",
                    label: "open-details",
                });
            },
            isOpen: false,
        };

        const sizeAndFitAccordion = {
            title: gettext("in_stock_product.size_and_fit"),
            children: (
                <VStack spacing="xs">
                    <Text textStyle="body-s" color="secondary" dataTestId="size-and-fit">
                        {gettext("in_stock_product.size_and_fit_text")}
                    </Text>
                    <Button
                        className={styles.sizeInfoButton}
                        title={gettext("in_stock_product.see_size_information")}
                        variant="secondary"
                        width="full"
                        href={leadHref}
                        onClick={onClick}
                        target="_blank"
                    />
                </VStack>
            ),
            onClick: () => {
                analytics.event({
                    category: "buy_area",
                    action: "clicked",
                    label: "size_and_fit_details",
                });
            },
            isOpen: true,
        };

        return [
            ...(removeSizeAndFitAccordion
                ? [productDetailsAccordion]
                : [sizeAndFitAccordion, productDetailsAccordion]),
            ...(product.designer_category_url
                ? [
                      {
                          title: gettext("in_stock_product.designer_category_link", {
                              designer: designer.name,
                          }),
                          href: product.designer_category_url,
                          onClick: () => {
                              analytics.event({
                                  category: "pdp_more_from_designer_btn",
                                  action: "clicked",
                                  itemData: { designer: designer.name },
                              });
                          },
                          isOpen: false,
                      },
                  ]
                : []),
        ];
    }, [
        buyOptions,
        designer.name,
        longDescription,
        product.designer_category_url,
        removeSizeAndFitAccordion,
    ]);

    const productDetails = (
        <View className={styles.productInfoWrapper} dataTestId="product-area-product-details">
            <VStack spacing="md">
                <ProductInfo />
                <div className={"hidden-desktop"}>
                    {relatedProductsSidebar && (
                        <MoreLikeThis moreLikeThisProducts={relatedProductsSidebar} />
                    )}
                </div>

                <div data-testid="accordion-contents">
                    <View>
                        <Accordion items={accordionContents} />
                    </View>
                </div>
            </VStack>
        </View>
    );

    const imageGallery = (
        <div className={styles.galleryParent} data-testid="product-area-image-gallery">
            <View className={styles.imageGallery}>
                <ProductImageGallery
                    images={product?.images?.map(({ alt_text, full_size_url, id }, i) => ({
                        id: id || `ProductImageGallery-${i.toString()}`,
                        altText: alt_text,
                        url: full_size_url,
                    }))}
                    productId={product.product_id}
                    buyOptionId={buyOptions[0].link_id.toString()}
                />
            </View>
        </div>
    );

    const sideBar = (
        <div className={styles.sidebarParent}>
            <View marginLeft={"sm"} marginTop={"sm"} className={styles.sidebarWrapper}>
                <RelatedProductsSidebar
                    relatedProductsSidebar={relatedProductsSidebar}
                    variant="vertical"
                    className={clsx(styles.sidebar, "affiliateSidebar")}
                    isEmo
                />
            </View>
        </div>
    );

    return (
        <Stack
            align={{ sm: "center", md: "center", lg: "start" }}
            direction={{ sm: "column", md: "column", lg: "row" }}
            spacing={isMobileViewport ? "md" : "xm"}
            justify="space-between"
        >
            {isMobileViewport || isTabletViewport || showGalleryOnLeft ? (
                <>
                    {imageGallery}
                    {productDetails}
                </>
            ) : (
                <>
                    {productDetails}
                    {imageGallery}
                </>
            )}
            {relatedProductsSidebar && sideBar}
        </Stack>
    );
}
